import { useEffect, useState } from 'react';

import { pickNextExercise } from '../exercises/pickNextExercise';
import { getChallengeExercises } from './leaderboardApi';
import type { ChallengeExercise, ChallengeExercises } from './leaderboardApi';

/**
 * Loads the live challenge's exercises for a course. `refreshKey` (e.g. the session's
 * `startedAt`) reloads them when the challenge restarts. Resolves to `null` when no challenge is
 * running, so callers can fall back to their regular behaviour.
 */
export function useChallengeExercises(courseId: string | null, refreshKey?: string) {
  const [challenge, setChallenge] = useState<ChallengeExercises | null>(null);

  useEffect(() => {
    if (!courseId) {
      return undefined;
    }

    let ignore = false;
    getChallengeExercises(courseId)
      .then((result) => {
        if (!ignore) {
          setChallenge(result);
        }
      })
      .catch(() => {
        if (!ignore) {
          setChallenge(null);
        }
      });
    return () => {
      ignore = true;
    };
  }, [courseId, refreshKey]);

  return challenge;
}

/**
 * Next exercise not yet passed during the challenge: after `currentId` in chapter order, wrapping
 * to earlier ones at the end. Without `currentId` it is simply the first one not yet passed.
 */
export function findNextChallengeExercise(
  exercises: ChallengeExercise[],
  currentId?: string,
): ChallengeExercise | undefined {
  return pickNextExercise(exercises, currentId, (exercise) => exercise.passedInChallenge);
}
